import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Clock, Certificate, Users, Star, CheckCircle, ArrowRight, CalendarBlank, MapPin, User, DownloadSimple } from "@phosphor-icons/react";
import Seo, { courseSchema, breadcrumbSchema, ORIGIN } from "@/components/Seo";
import ShareBar from "@/components/ShareBar";
import WaitlistButton from "@/components/WaitlistButton";
import PayIn4 from "@/components/PayIn4";

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add, items, selectSession } = useCart();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [chosenSession, setChosenSession] = useState(null);

  useEffect(() => {
    setLoading(true);
    setChosenSession(null);
    api.get(`/courses/${slug}`).then(({ data }) => setCourse(data)).catch(() => setCourse(null)).finally(() => setLoading(false));
    api.get(`/courses/${slug}/sessions`).then(({ data }) => setSessions(data)).catch(() => setSessions([]));
  }, [slug]);

  if (loading) return <div className="container-x py-32 font-mono text-sm text-neutral-500">Loading...</div>;
  if (!course) return <div className="container-x py-32 font-mono text-sm">Course not found. <Link to="/training" className="text-[#FFD300]">Back to catalog</Link></div>;

  const inCart = items.some(i => i.id === course.id);

  const handleEnroll = () => {
    if (!inCart) add(course);
    if (chosenSession && selectSession) selectSession(course.id, chosenSession);
    navigate("/cart");
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "America/New_York" });
  };
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" });

  return (
    <div data-testid="course-detail-page">
      <Seo
        title={course.title}
        description={course.short_description}
        path={`/training/${course.slug}`}
        image={course.share_image || course.image}
        type="article"
        schema={[
          courseSchema({
            title: course.title,
            description: course.short_description,
            url: `${ORIGIN}/training/${course.slug}`,
            image: course.image,
            instructor: course.instructor,
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Training", path: "/training" },
            { name: course.title, path: `/training/${course.slug}` },
          ]),
        ]}
      />
      {/* HERO */}
      <section className="border-b border-white/10 pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          {course.state ? (
            <div className="w-full h-full bg-gradient-to-br from-[#FFD300] to-black" />
          ) : (
            <>
              <img src={course.image} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/40" />
            </>
          )}
        </div>
        <div className="container-x relative">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-9">
              <div className="flex items-center gap-2 mb-6">
                <span className="code-badge">{course.code}</span>
                {course.state && <span className="state-tag">{course.state}</span>}
                <span className="state-tag">{course.category}</span>
              </div>
              <h1 className="font-heading text-4xl md:text-6xl tracking-tighter leading-[0.95] max-w-4xl mb-6" data-testid="course-title">{course.title}</h1>
              <p className="text-neutral-300 leading-relaxed max-w-3xl text-lg mb-8">{course.short_description}</p>
              <div className="flex flex-wrap gap-6 text-sm text-neutral-300">
                <span className="flex items-center gap-2"><Clock size={16} className="text-[#FFD300]" /> {course.duration_hours} hours</span>
                <span className="flex items-center gap-2"><Certificate size={16} className="text-[#FFD300]" /> {course.level}</span>
                <span className="flex items-center gap-2"><Users size={16} className="text-[#FFD300]" /> {course.students_count} students</span>
                <span className="flex items-center gap-2"><Star size={16} weight="fill" className="text-[#FFD300]" /> {course.rating} rating</span>
              </div>
              <div className="mt-6">
                <ShareBar slug={course.slug} title={course.title} />
              </div>
            </div>
            {course.state || course.slug === "first-aid-cpr-aed" ? (
              <div className="hidden lg:flex col-span-3 justify-end">
                <div className="w-48 h-48 bg-gradient-to-br from-white to-neutral-100 rounded-full p-5 shadow-2xl shadow-[#FFD300]/20" data-testid="course-state-seal">
                  <img src={course.image} alt={`${course.state || "course"} seal`} className="w-full h-full object-contain" />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="section-pad">
        <div className="container-x grid grid-cols-12 gap-12">
          <div className="col-span-12 lg:col-span-8 space-y-16">
            <div data-testid="course-overview">
              <div className="overline mb-4">// OVERVIEW</div>
              <p className="text-lg text-neutral-200 leading-relaxed">{course.long_description}</p>
            </div>

            <div data-testid="course-curriculum">
              <div className="overline mb-4">// CURRICULUM</div>
              <h2 className="font-heading text-3xl tracking-tighter mb-8">What you will learn</h2>
              <div className="space-y-4">
                {course.curriculum?.map((mod, i) => (
                  <div key={i} className="surface-card p-6" data-testid={`module-${i}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="font-mono text-xs text-[#FFD300]">M{String(i+1).padStart(2,'0')}</div>
                      <h3 className="font-heading text-xl tracking-tight">{mod.module}</h3>
                    </div>
                    <ul className="space-y-2">
                      {mod.lessons.map((l, j) => (
                        <li key={j} className="flex items-center justify-between text-sm py-2 border-t border-white/5">
                          <span className="text-neutral-300 flex items-center gap-3">
                            <span className="font-mono text-xs text-neutral-500">L{i+1}.{j+1}</span>
                            {l.title}
                          </span>
                          <span className="font-mono text-xs text-neutral-500">{l.duration}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="course-requirements">
              <div className="overline mb-4">// REQUIREMENTS</div>
              <h2 className="font-heading text-3xl tracking-tighter mb-6">Prerequisites</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {course.requirements?.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-neutral-300">
                    <CheckCircle size={18} weight="duotone" className="text-[#FFD300] mt-0.5 flex-shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>

            {course.certification && (
              <div className="surface-card p-8" data-testid="course-certification">
                <div className="overline mb-3">// CERTIFICATION</div>
                <div className="flex items-start gap-4">
                  <Certificate size={40} weight="duotone" className="text-[#FFD300]" />
                  <div>
                    <h3 className="font-heading text-2xl tracking-tight mb-2">Upon successful completion</h3>
                    <p className="text-neutral-300">{course.certification}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Upcoming class dates */}
            <div data-testid="schedule-section">
              <div className="overline mb-4">// UPCOMING CLASS DATES</div>
              <h2 className="font-heading text-3xl tracking-tighter mb-6">Pick a class date</h2>
              {sessions.length === 0 ? (
                <div className="surface-card p-8 font-mono text-sm" data-testid="no-sessions-notice">
                  {course.manual_schedule ? (
                    <>
                      <div className="text-[#FFD300] mb-2">// CLASS DATES COMING SOON</div>
                      <div className="text-neutral-300">Enroll now to reserve your seat — our operations team will email you the schedule as soon as dates are posted.</div>
                    </>
                  ) : (
                    <span className="text-neutral-500">No upcoming sessions. Contact ops to schedule.</span>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {sessions.map((s) => {
                    const full = s.enrolled_count >= s.capacity;
                    const picked = chosenSession?.id === s.id;
                    const seatsLeft = s.capacity - s.enrolled_count;
                    return (
                      <div key={s.id}>
                      <button
                        onClick={() => !full && setChosenSession(s)}
                        disabled={full}
                        data-testid={`session-${s.id}`}
                        className={`w-full p-5 border text-left flex items-center justify-between gap-6 transition-colors ${
                          picked ? "bg-[#FFD300] text-black border-[#FFD300]" :
                          full ? "bg-[#0A0A0A] border-white/5 opacity-60 cursor-not-allowed" :
                          "bg-[#0A0A0A] border-white/10 hover:border-[#FFD300]/50 hover:bg-[#141414]"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`font-mono text-xs px-3 py-1 ${picked ? "bg-black text-[#FFD300]" : "bg-[#FFD300]/10 text-[#FFD300] border border-[#FFD300]/30"}`}>
                            <CalendarBlank size={12} className="inline mr-1" />
                            {fmtDate(s.starts_at)}
                          </div>
                          <div>
                            <div className={`font-heading text-base ${picked ? "" : "text-white"}`}>Starts {fmtTime(s.starts_at)} ET · {course.duration_hours} hrs</div>
                            <div className={`text-xs flex items-center gap-3 mt-1 ${picked ? "text-black/70" : "text-neutral-400"}`}>
                              <span className="flex items-center gap-1"><MapPin size={11} /> {s.venue}</span>
                              <span className="flex items-center gap-1"><User size={11} /> {s.instructor}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {full ? (
                            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-red-400">Sold out</div>
                          ) : (
                            <div className={`font-mono text-[10px] tracking-[0.2em] uppercase ${picked ? "text-black" : seatsLeft <= 3 ? "text-orange-400" : "text-neutral-400"}`}>
                              {seatsLeft} seat{seatsLeft !== 1 ? "s" : ""} left
                            </div>
                          )}
                          {picked && <div className="font-mono text-[10px] tracking-[0.2em] uppercase mt-1">✓ Selected</div>}
                        </div>
                      </button>
                      {full && (
                        <div className="px-5 py-3 border border-t-0 border-white/10 bg-[#0A0A0A]" data-testid={`waitlist-row-${s.id}`}>
                          <WaitlistButton session={s} />
                        </div>
                      )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-4 font-mono text-xs text-neutral-500">
                Selecting a date is optional — you can enroll first and pick a session later from your dashboard.
              </div>
            </div>

            {/* Download brief */}
            {course.state && (
              <div className="surface-card p-8 flex items-center justify-between gap-6 flex-wrap" data-testid="download-pdf-block">
                <div className="flex items-center gap-4">
                  <DownloadSimple size={36} weight="duotone" className="text-[#FFD300] flex-shrink-0" />
                  <div>
                    <div className="overline mb-1">// REQUIREMENTS BRIEF</div>
                    <h3 className="font-heading text-xl tracking-tight">Download the {course.state} Permit Requirements PDF</h3>
                    <p className="text-neutral-400 text-sm mt-1">Age, fees, residency, processing time, disqualifiers — everything in one document.</p>
                  </div>
                </div>
                <a
                  href={`${API}/courses/${course.slug}/requirements.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  data-testid="download-pdf-btn"
                >
                  Download PDF <DownloadSimple size={14} />
                </a>
              </div>
            )}
          </div>

          <aside className="col-span-12 lg:col-span-4">
            <div className="sticky top-24 surface-card p-8" data-testid="enroll-panel">
              <div className="overline mb-3">// ENROLLMENT</div>
              <div className="font-heading text-5xl mb-1">${course.price.toFixed(0)}</div>
              <div className="font-mono text-xs text-neutral-500 mb-1">USD · One-time payment</div>
              <PayIn4 price={course.price} className="mb-6" />

              {chosenSession && (
                <div className="border border-[#FFD300]/40 bg-[#FFD300]/5 p-3 mb-4" data-testid="chosen-session-summary">
                  <div className="overline text-[#FFD300] mb-1">// SESSION SELECTED</div>
                  <div className="font-mono text-xs text-white">{fmtDate(chosenSession.starts_at)}</div>
                  <div className="font-mono text-[10px] text-neutral-400">{chosenSession.venue}</div>
                </div>
              )}

              <button onClick={handleEnroll} className="btn-primary w-full justify-center mb-3" data-testid="enroll-now-btn">
                {inCart ? "View Cart" : "Enroll Now"} <ArrowRight size={14} />
              </button>
              <Link to="/cart" className="btn-secondary w-full justify-center mb-6" data-testid="view-cart-btn">View Cart →</Link>

              <div className="divider-line my-6" />

              <div className="space-y-3 text-sm text-neutral-300">
                <div className="flex items-center justify-between"><span className="text-neutral-500 font-mono text-xs uppercase">Duration</span><span>{course.duration_hours} hrs</span></div>
                <div className="flex items-center justify-between"><span className="text-neutral-500 font-mono text-xs uppercase">Level</span><span>{course.level}</span></div>
                <div className="flex items-center justify-between"><span className="text-neutral-500 font-mono text-xs uppercase">Code</span><span className="font-mono text-xs">{course.code}</span></div>
                {course.state && <div className="flex items-center justify-between"><span className="text-neutral-500 font-mono text-xs uppercase">State</span><span>{course.state}</span></div>}
                <div className="flex items-center justify-between"><span className="text-neutral-500 font-mono text-xs uppercase">Instructor</span><span>{course.instructor}</span></div>
              </div>

              <div className="divider-line my-6" />

              <div className="text-xs text-neutral-500 leading-relaxed">
                Secure card checkout · Instant enrollment confirmation · Certificate issued upon completion
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
